/**
 * Whether the start step should commit a `?team_id=` link on its own and move
 * the reader on, instead of waiting for Next.
 *
 * For a player, a team is the whole answer: it carries its division, and only
 * players join an existing roster, so the link settles the role too. Every
 * rule below is a case where that stops being true, or where advancing would
 * trap the reader.
 */

import { REGISTRATION_ROLE_TEAM } from '../registrationUrlParams'
import { divisionSelectableForRole } from '../startStep/divisionRole'
import { teamMatchesId } from '../startStep/teamDisplay'
import type { DivisionTeam, StartDivision } from '../startStep/types'

export type LinkAutoAdvance = 'none' | 'advance'

export const linkAutoAdvance = <T extends StartDivision>({
  entry,
  roleParam,
  sessionTeamId,
  isLogged,
  isTeamFull,
}: {
  /** The linked team resolved against `registration.teams`; null when unknown. */
  entry: DivisionTeam<T> | null | undefined
  /**
   * Whether this reader is refused by the team (`isTeamFullFor`): a full roster,
   * or Team Budget slots all taken — unless their own invite holds one.
   */
  isTeamFull: (entry: DivisionTeam<T>) => boolean
  /** The `?type=` param, when present. */
  roleParam: string | null | undefined
  /** The team the stored session already holds. */
  sessionTeamId: string | null | undefined
  isLogged: boolean
}): LinkAutoAdvance => {
  // An id the registration does not know — the ordinary picker takes over.
  if (!entry) {
    return 'none'
  }
  // The existing pre-select refuses a full team with its own notice.
  if (isTeamFull(entry)) {
    return 'none'
  }
  // A rep never carries a team out of the start step, so a link that also
  // says `type=team` is a rep link and follows today's flow.
  if (roleParam === REGISTRATION_ROLE_TEAM) {
    return 'none'
  }
  // The link implies a player; a division that takes none cannot be joined.
  if (!divisionSelectableForRole(entry.division, 'player')) {
    return 'none'
  }
  // The session already holds this team and the reader has not signed in:
  // they came BACK from sign-up. Advancing again would bounce them straight
  // forward, with no way to change the team they are signing up for.
  // Verification is deliberately not read here — `userInfo` is stale after
  // email confirmation; the commit's own check decides where a signed-in
  // reader lands.
  if (!isLogged && teamMatchesId(entry.team, sessionTeamId)) {
    return 'none'
  }
  return 'advance'
}
